import { useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Field colors come from CSS variables (no hardcoded hex here)
  const fieldClass =
    'w-full px-4 py-3 rounded-2xl border bg-[var(--section-glass)] border-[rgba(15,23,42,0.14)] dark:border-white/10 text-[color:var(--page-fg-solid)] placeholder:opacity-60 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15 focus:border-transparent';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: formData.get('name')?.toString() || '',
      email: formData.get('email')?.toString() || '',
      service: formData.get('service')?.toString() || '',
      date: formData.get('date')?.toString() || '',
      message: formData.get('message')?.toString() || '',
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      setSubmitStatus('success');
      form.reset();
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 bg-[var(--page-bg)] text-[color:var(--page-fg-solid)]">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-center text-[color:var(--page-fg-solid)]">
          Kontakt &amp; Anfrage
        </h2>

        <p className="text-base md:text-lg mb-6 text-center text-[color:var(--page-fg-80)]">
          Starten Sie mit einer Anfrage. Wir prüfen Ihr Objekt, klären Umfang und Intervalle
          und melden uns mit dem nächsten Schritt.
        </p>

        <div className="mt-2 grid gap-10 md:grid-cols-[1.05fr,1.15fr] items-start">
          <div className="space-y-6 text-center">
            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                href="tel:+4915217782301"
                size="md"
                className="gap-2 px-6 py-3 rounded-full bg-[color:var(--accent-solid)] text-white border border-[color:rgba(var(--accent),0.55)] shadow-[0_12px_30px_rgba(15,23,42,0.18)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.22)] tracking-[0.18em] uppercase text-[0.7rem]"
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                Direkt anrufen
              </Button>
              <Button
                href="mailto:info@myclean-service.de"
                size="md"
                className="gap-2 px-6 py-3 rounded-full bg-[color:var(--accent-solid)] text-white border border-[rgba(var(--accent),0.55)] shadow-[0_12px_30px_rgba(15,23,42,0.18)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.22)] tracking-[0.18em] uppercase text-[0.7rem]"
              >
                <Mail className="h-5 w-5" aria-hidden="true" />
                E-Mail schreiben
              </Button>
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl md:text-3xl font-semibold text-center text-[color:var(--page-fg-solid)]">
                So läuft Ihre Anfrage
              </h3>
              <p className="text-base leading-relaxed text-center text-[color:var(--page-fg-80)]">
                Über das Formular können Sie uns kurz Ihr Objekt und den gewünschten Reinigungsumfang schildern. Wir
                prüfen Ihre Angaben und melden uns mit Rückfragen, einem Vorschlag oder einem Rückruf.
              </p>
            </div>

            <ul className="space-y-2 text-sm md:text-base text-center text-[color:var(--page-fg-80)]">
              <li className="flex items-start gap-2 justify-center">
                <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[color:var(--accent-solid)]" />
                <span>Regelmäßige Unterhaltsreinigung für Büros, Praxen, Treppenhäuser und Objekte in Berlin.</span>
              </li>
              <li className="flex items-start gap-2 justify-center">
                <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[color:var(--accent-solid)]" />
                <span>Flexible Intervalle und klare Zuständigkeiten – abgestimmt auf Nutzung und Anforderungen.</span>
              </li>
              <li className="flex items-start gap-2 justify-center">
                <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[color:var(--accent-solid)]" />
                <span>
                  Für kurzfristige Rückfragen erreichen Sie uns am besten telefonisch, für Angebote nutzen Sie das
                  Formular.
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-3xl border border-[rgba(15,23,42,0.14)] dark:border-white/10 bg-[var(--card-glass)] shadow-[0_18px_60px_rgba(15,61,140,0.22)] px-6 py-7 md:px-7 md:py-8">
            <form className="grid gap-6 md:grid-cols-2 text-left" onSubmit={handleSubmit}>
              <div className="md:col-span-2">
                <p className="text-sm text-[color:var(--page-fg-80)]">
                  Über dieses Formular erreichen Sie myclean für Angebote und Rückfragen rund um Ihre Reinigung. Wir
                  prüfen Ihre Angaben und melden uns mit Rückfragen, einem Vorschlag oder einem Rückruf.
                </p>
              </div>

              <div>
                <label htmlFor="contact-name" className="block mb-2 text-sm font-medium text-[color:var(--page-fg-solid)]">Name*</label>
                <input id="contact-name" type="text" required className={fieldClass} name="name" autoComplete="name" />
              </div>

              <div>
                <label htmlFor="contact-email" className="block mb-2 text-sm font-medium text-[color:var(--page-fg-solid)]">E-Mail*</label>
                <input id="contact-email" type="email" required className={fieldClass} name="email" autoComplete="email" />
              </div>

              <div>
                <label htmlFor="contact-service" className="block mb-2 text-sm font-medium text-[color:var(--page-fg-solid)]">Anliegen*</label>
                <select id="contact-service" required className={fieldClass} style={{ backgroundPosition: 'right 1rem center' }} name="service">
                  <option value="">Bitte auswählen</option>
                  <option value="unterhaltsreinigung">Unterhaltsreinigung Büro / Praxis</option>
                  <option value="treppenhausreinigung">Treppenhausreinigung</option>
                  <option value="glasreinigung">Glas- &amp; Fensterreinigung</option>
                  <option value="sonderreinigung">Sonderreinigung / Grundreinigung</option>
                  <option value="wohnanlage-gewerbe">Wohnanlage / Gewerbeobjekt</option>
                  <option value="sonstiges">Sonstiges Anliegen</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-date" className="block mb-2 text-sm font-medium text-[color:var(--page-fg-solid)]">Wunschtermin (optional)</label>
                <input id="contact-date" type="date" className={fieldClass} name="date" />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="block mb-2 text-sm font-medium text-[color:var(--page-fg-solid)]">Nachricht*</label>
                <textarea
                  id="contact-message"
                  required
                  rows={4}
                  className={fieldClass}
                  placeholder="z. B. Objektart, Größe, Reinigungsumfang, gewünschte Intervalle, Adresse und Telefonnummer"
                  name="message"
                />
              </div>

              <p className="md:col-span-2 text-xs leading-relaxed text-[color:var(--page-fg-70)]">
                Mit dem Absenden verarbeiten wir Ihre Angaben zur Bearbeitung der Anfrage. Die Verarbeitung erfolgt
                je nach Anliegen zur Durchführung vorvertraglicher Maßnahmen oder auf Grundlage unseres berechtigten
                Interesses an der Beantwortung Ihrer Nachricht. Details finden Sie in der{' '}
                <Link to="/datenschutz" className="font-semibold underline underline-offset-2">
                  Datenschutzerklärung
                </Link>
                .
              </p>

              <div className="md:col-span-2 flex justify-end">
                <button
                  type="submit"
                  className="min-w-[11rem] rounded-full border border-[rgba(var(--accent),0.55)] bg-[color:var(--accent-solid)] px-7 py-3 text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_rgba(15,23,42,0.18)] transition-[background-color,border-color,box-shadow,filter] duration-300 hover:shadow-[0_22px_55px_rgba(15,23,42,0.22)] sm:text-sm md:text-base"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Wird gesendet…' : 'Nachricht senden'}
                </button>
              </div>

              <div className="min-h-5 md:col-span-2 text-right text-sm">
                {submitStatus === 'success' && (
                  <span className="text-emerald-500">Deine Anfrage wurde erfolgreich gesendet.</span>
                )}
                {submitStatus === 'error' && (
                  <span className="text-red-500">
                    Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
